import type { KeyboardEvent } from "react";
import { tierLabel } from "@chula/shared";
import type { CampusEnergySnapshot, BuildingEnergyReading } from "../lib/energyModel";
import { CHAM5 } from "../lib/cubems";
import { ACADEMIC_EUI_BAND } from "../lib/campusMetrics";
import { PanelHeader } from "./PanelHeader";

/**
 * EnergyPanel — campus aggregate view that pairs with the heatmap.
 *
 * Leads with the MEASURED CU-BEMS block (Chamchuri 5, metered 2019 — the one
 * building where the number is real), then the modeled campus aggregate:
 * total draw (kW), 24h kWh, solar offset, net grid import, top consumers,
 * and the anomaly list. Reads from the same readings the map colors with,
 * so panel + map can never disagree.
 *
 * Tags every modeled number as "synthetic" until BEMS is wired — the
 * measured/modeled split is the partnership story leadership cares about.
 */

interface Props {
  snapshot: CampusEnergySnapshot;
  ghostMode?: boolean;
  onToggleGhostMode?: () => void;
  onSelectBuilding?: (b: BuildingEnergyReading) => void;
}

function fmtKw(kw: number): string {
  if (kw >= 1000) return `${(kw / 1000).toFixed(2)} MW`;
  return `${Math.round(kw)} kW`;
}
function fmtKwh(kwh: number): string {
  if (kwh >= 1_000_000) return `${(kwh / 1_000_000).toFixed(2)} GWh`;
  if (kwh >= 1000) return `${(kwh / 1000).toFixed(1)} MWh`;
  return `${Math.round(kwh)} kWh`;
}
function flagColor(flag: string): string {
  if (flag === "stuck-cooling") return "var(--bad)";
  if (flag === "possible-outage") return "var(--bad)";
  if (flag === "elevated") return "var(--warn)";
  if (flag === "after-hours-load") return "var(--warn)";
  return "var(--data)";
}

function fmtTons(kg: number): string {
  if (Math.abs(kg) >= 1000) return `${(kg / 1000).toFixed(1)} t`;
  return `${Math.round(kg)} kg`;
}

// ─── CU-BEMS measured block ───────────────────────────────────────────────
// The flagship "this number is measured, not modeled" section: Chamchuri 5,
// metered at 1-min resolution across 2019 (see lib/cubems.ts). Reference
// tier — real historical data, distinct from the scenario-tier model below.

const SPARK_W = 240;
const SPARK_H = 40;
const SPARK_PAD = 3;

/** Polyline points for a 24h profile, normalized into the sparkline viewBox. */
function sparkPoints(values: number[]): string {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const stepX = SPARK_W / (values.length - 1);
  return values
    .map((v, i) => {
      const x = i * stepX;
      const y = SPARK_H - SPARK_PAD - ((v - min) / span) * (SPARK_H - SPARK_PAD * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function CubemsMeasuredBlock() {
  const { ac, lighting, plug } = CHAM5.endUseSharePct;
  const weekday = CHAM5.hourlyKwWeekday;
  const peakHour = weekday.indexOf(Math.max(...weekday));
  const eui = CHAM5.euiKwhM2Yr;
  const isWithinBand = eui >= ACADEMIC_EUI_BAND.low && eui <= ACADEMIC_EUI_BAND.high;
  const citeHost = new URL(CHAM5.dataUrl).host;
  return (
    <section className="cubems-block" aria-label="Measured CU-BEMS energy data — Chamchuri 5">
      <header className="cubems-head">
        <span className="eyebrow mono">MEASURED — CU-BEMS · CHAMCHURI 5</span>
        <span
          className="cubems-chip mono"
          title={`Metered ${CHAM5.year} dataset (${(CHAM5.minuteSamples / 1e6).toFixed(2)}M 1-min samples, ${CHAM5.floors} floors) — ${tierLabel("reference")} tier: real historical measurement, not live, not modeled`}
        >
          REAL {CHAM5.year}
        </span>
      </header>

      <div className="cubems-grid">
        <div className="cubems-cell">
          <span className="cubems-cell-lbl mono">ANNUAL</span>
          <span className="cubems-cell-val mono">{fmtKwh(CHAM5.annualKwh)}</span>
          <span className="cubems-cell-sub mono">{CHAM5.annualKwh.toLocaleString("en-US")} kWh</span>
        </div>
        <div className="cubems-cell">
          <span className="cubems-cell-lbl mono">EUI</span>
          <span className="cubems-cell-val mono">{eui.toFixed(1)}</span>
          <span className="cubems-cell-sub mono">kWh/m²/yr</span>
        </div>
        <div className="cubems-cell">
          <span className="cubems-cell-lbl mono">PEAK HOURLY AVG</span>
          <span className="cubems-cell-val mono">{fmtKw(CHAM5.peakHourlyAvgKw)}</span>
          <span className="cubems-cell-sub mono">weekday {String(peakHour).padStart(2, "0")}:00</span>
        </div>
        <div className="cubems-cell">
          <span className="cubems-cell-lbl mono">END USE</span>
          <span className="cubems-cell-val mono">{Math.round(ac)}/{Math.round(lighting)}/{Math.round(plug)}</span>
          <span className="cubems-cell-sub mono">AC/light/plug %</span>
        </div>
      </div>

      <div className="cubems-spark">
        <svg
          viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`Typical weekday hourly kW profile, ${CHAM5.year}`}
        >
          <polyline
            points={sparkPoints(weekday)}
            fill="none"
            stroke="var(--accent)"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <span className="cubems-spark-label mono">TYPICAL WEEKDAY kW · {CHAM5.year}</span>
      </div>

      <p
        className="cubems-calib mono"
        title="Calibration check: the campus model assumes this EUI band for academic buildings; CU-BEMS is the metered anchor"
      >
        Model band {ACADEMIC_EUI_BAND.low}–{ACADEMIC_EUI_BAND.high} kWh/m²/yr (academic) ·
        measured {eui.toFixed(1)} — {isWithinBand ? "within band" : "outside band"}
      </p>
      <p className="cubems-cite caption" title={`${CHAM5.citation} · ${CHAM5.dataUrl}`}>
        {CHAM5.citation} · {citeHost}
      </p>
    </section>
  );
}

export function EnergyPanel({ snapshot, ghostMode, onToggleGhostMode, onSelectBuilding }: Props) {
  const {
    totalKw, totalDailyKwh, buildingCount, anomalyCount, topConsumers, anomalies,
    solarKw, netGridKw,
    carbonGridKgPerDay, carbonSolarAvoidedKgPerDay, carbonTreesAbsorbedKgPerDay,
    carbonNetKgPerDay, carbonNeutralityPct,
  } = snapshot;
  const solarPct = totalKw > 0 ? Math.round((solarKw / totalKw) * 100) : 0;
  // Keyboard equivalent for the clickable building rows (a11y).
  const rowKeyDown = (b: BuildingEnergyReading) => (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelectBuilding?.(b);
    }
  };
  const neutralColor =
    carbonNeutralityPct >= 100 ? "var(--good)" :
    carbonNeutralityPct >= 50  ? "var(--warn)" :
                                  "var(--bad)";
  return (
    <div className="energy-panel">
      <PanelHeader
        title="ENERGY · CAMPUS"
        fallbackTier="scenario"
        source="eui-model"
      />
      <CubemsMeasuredBlock />
      <header className="energy-panel-head">
        <span className="lcars-bar" />
        <button
          className="mono caption"
          title="Fade every building except the ones drawing power with nobody inside — after-hours load, cooling left running. Modeled until BEMS meters land."
          aria-pressed={ghostMode}
          style={{
            background: ghostMode ? "var(--bad)" : "transparent",
            color: ghostMode ? "var(--paper)" : "var(--bad)",
            border: "1px solid var(--bad)",
            padding: "2px 6px",
            cursor: "pointer",
            marginLeft: "auto",
          }}
          onClick={onToggleGhostMode}
        >
          {ghostMode ? "GHOST CONSUMPTION: ON" : "HIGHLIGHT GHOST CONSUMPTION"}
        </button>
      </header>

      <div className="energy-bignum">
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">DRAW NOW</span>
          <span className="energy-stat-val mono">{fmtKw(totalKw)}</span>
          <span className="energy-stat-sub mono">across {buildingCount} buildings</span>
        </div>
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">SOLAR PV</span>
          <span className="energy-stat-val mono" style={{ color: "var(--good)" }}>{fmtKw(solarKw)}</span>
          <span className="energy-stat-sub mono">{solarPct}% of load · CU-MEA microgrid</span>
        </div>
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">NET GRID</span>
          <span className="energy-stat-val mono" style={{ color: netGridKw > 0 ? "var(--warn)" : "var(--good)" }}>
            {netGridKw > 0 ? "+" : ""}{fmtKw(netGridKw)}
          </span>
          <span className="energy-stat-sub mono">{netGridKw > 0 ? "import" : "export"}</span>
        </div>
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">24H</span>
          <span className="energy-stat-val mono">{fmtKwh(totalDailyKwh)}</span>
          <span className="energy-stat-sub mono">projected · ~{Math.round(totalDailyKwh * 4 / 1000)} ฿k @ ฿4/kWh</span>
        </div>
      </div>

      <section className="energy-section">
        <header className="energy-section-head">
          <span className="dot" style={{ background: neutralColor }} />
          <span className="eyebrow mono">Carbon ledger · today</span>
          <span className="mono caption" style={{ color: neutralColor }}>
            {carbonNeutralityPct}% to net-zero
          </span>
        </header>
        <div className="energy-carbon-grid">
          <div className="energy-carbon-cell" style={{ borderLeftColor: "var(--bad)" }}>
            <span className="energy-stat-lbl mono">GRID EMISSIONS</span>
            <span className="energy-stat-val mono">{fmtTons(carbonGridKgPerDay)} CO₂</span>
            <span className="energy-stat-sub mono">EGAT factor 0.428 kg/kWh</span>
          </div>
          <div className="energy-carbon-cell" style={{ borderLeftColor: "var(--good)" }}>
            <span className="energy-stat-lbl mono">SOLAR AVOIDED</span>
            <span className="energy-stat-val mono">−{fmtTons(carbonSolarAvoidedKgPerDay)} CO₂</span>
            <span className="energy-stat-sub mono">5.4 MW PV × 5 sun-h/day</span>
          </div>
          <div className="energy-carbon-cell" style={{ borderLeftColor: "var(--good)" }}>
            <span className="energy-stat-lbl mono">TREES ABSORB</span>
            <span className="energy-stat-val mono">−{fmtTons(carbonTreesAbsorbedKgPerDay)} CO₂</span>
            <span className="energy-stat-sub mono">4,107 trees @ 30 kg/yr each</span>
          </div>
          <div className="energy-carbon-cell" style={{ borderLeftColor: neutralColor }}>
            <span className="energy-stat-lbl mono">NET TODAY</span>
            <span className="energy-stat-val mono" style={{ color: neutralColor }}>
              {carbonNetKgPerDay > 0 ? "+" : ""}{fmtTons(carbonNetKgPerDay)} CO₂
            </span>
            <span className="energy-stat-sub mono">
              {carbonNetKgPerDay > 0 ? `gap to neutral` : `surplus over neutral`}
            </span>
          </div>
        </div>
      </section>

      {anomalies.length > 0 && (
        <section className="energy-section">
          <header className="energy-section-head">
            <span className="dot unavailable" />
            <span className="eyebrow mono" style={{ color: "var(--bad)" }}>
              {anomalyCount} anomal{anomalyCount === 1 ? "y" : "ies"} flagged
            </span>
          </header>
          <ul className="energy-list">
            {anomalies.map((b) => (
              <li
                key={b.buildingId}
                className="energy-row energy-row-anomaly"
                role={onSelectBuilding ? "button" : undefined}
                tabIndex={onSelectBuilding ? 0 : -1}
                onClick={onSelectBuilding ? () => onSelectBuilding(b) : undefined}
                onKeyDown={onSelectBuilding ? rowKeyDown(b) : undefined}
              >
                <span className="energy-row-name">{b.name ?? b.buildingId}</span>
                <span className="energy-row-kw mono">{fmtKw(b.currentKw)}</span>
                <span className="energy-row-vs mono">×{b.vsAvg.toFixed(1)}</span>
                <span className="energy-row-flags mono">
                  {b.flags.map((f) => (
                    <span key={f} style={{ color: flagColor(f) }}>{f}</span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="energy-section">
        <header className="energy-section-head">
          <span className="eyebrow mono">Top consumers · modeled</span>
        </header>
        <ul className="energy-list">
          {topConsumers.map((b) => (
            <li
              key={b.buildingId}
              className="energy-row"
              role={onSelectBuilding ? "button" : undefined}
              tabIndex={onSelectBuilding ? 0 : -1}
              onClick={onSelectBuilding ? () => onSelectBuilding(b) : undefined}
              onKeyDown={onSelectBuilding ? rowKeyDown(b) : undefined}
            >
              <span className="energy-row-name">{b.name ?? b.buildingId}</span>
              <span className="energy-row-kw mono">{fmtKw(b.currentKw)}</span>
              <span className="energy-row-vs mono">{b.buildingType}</span>
              <span className="energy-row-area mono">{(b.floorAreaM2 / 1000).toFixed(1)}km²</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Provenance footer — every number above is modeled, not metered. */}
      <div
        className="caption"
        style={{
          fontSize: "0.68rem",
          color: "var(--text-3)",
          lineHeight: 1.5,
          paddingTop: 8,
          borderTop: "1px dashed var(--line)",
        }}
      >
        EUI × floor-area × diurnal-curve model. Not metered — BEMS integration
        pending. Anomalies are simulated examples.
      </div>
    </div>
  );
}
